import { Alert, Number, Steps } from "components/common"
import cs from "./index.module.less"
import { Separator } from "components/custom"
import { memo, useCallback, useMemo } from "react"
import { t } from "i18next"
import { setProgressionBar } from "../../../../utils"
import cx from "clsx"
import { convertToReadableString } from "utils"
import { numberProps } from "../../../../utils/constants"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"

const AddSaleStepper = () => {
  const { addSalesApis, stateManager, formWatchers, minTokenDetails, vasEnabled, vasFees } = useAddSalesContext()
  const { project, unit, cdpCommitment, amount } = formWatchers
  const { leadSalesInfo } = addSalesApis

  const calculateValue = useCallback(
    (value, unit, fallback) =>
      value !== undefined ? (unit === "percentage" ? (value / 100) * stateManager?.dealPrice : value) : fallback,
    [stateManager?.dealPrice]
  )

  const getCurrent = useMemo(() => {
    return setProgressionBar(amount, project, stateManager?.dealPrice, minTokenDetails, cdpCommitment)
  }, [amount, project, stateManager?.dealPrice, minTokenDetails])

  const stepData = useMemo(() => {
    const token = calculateValue(
      leadSalesInfo?.token,
      leadSalesInfo?.tokenUnit,
      calculateValue(minTokenDetails.minToken, minTokenDetails.minTokenUnit, 0)
    )
    const pdp = Math.round((leadSalesInfo?.pdpPercentage ?? project?.pdpPercentage) * stateManager?.dealPrice)
    const cdp = Math.round(
      ((leadSalesInfo?.cdp || cdpCommitment) ?? project?.ProjectFinancial?.completeDownPayment) *
        0.01 *
        stateManager?.dealPrice
    )

    return [
      ...(vasEnabled
        ? [
            {
              title: t("BSF Fee"),
              description:
                vasFees || leadSalesInfo?.vasRemianingAmount  ? (
                  <>
                    {`${t("BSF Fee")}: `}
                    <Number
                      customToolTip={{
                        title: leadSalesInfo?.isVasRemaining ? leadSalesInfo?.vasRemianingAmount : vasFees,
                      }}
                      {...numberProps}
                      value={leadSalesInfo?.isVasRemaining ? leadSalesInfo?.vasRemianingAmount : vasFees}
                    />
                  </>
                ) : (
                  t("Required to lock the unit")
                ),
            },
          ]
        : [
            {
              title: t("Token"),
              description:
                leadSalesInfo?.tokenUnit === "percentage" || !minTokenDetails?.tokenWithUnit > 1 ? (
                  <>
                    {`${t("Min Token")}: ${leadSalesInfo?.token || minTokenDetails?.minToken}% `}
                    <Separator />
                    <Number customToolTip={{ title: token }} {...numberProps} value={token} />
                  </>
                ) : leadSalesInfo?.current >= 0 || !!project ? (
                  <>
                    {`${t("Min Token")}: `}
                    <Number customToolTip={{ title: token }} {...numberProps} value={token} />
                  </>
                ) : (
                  ""
                ),
            },
          ]),
      {
        title: t("PDP"),
        description:
          leadSalesInfo?.current >= 0 || !!project ? (
            <>
              <>{`${t("PDP")}: ${leadSalesInfo?.pdp ?? project?.partialDp}% `}</>
              <Separator />
              <Number
                customToolTip={{
                  title: pdp,
                }}
                {...numberProps}
                value={pdp}
              />
            </>
          ) : (
            ""
          ),
      },
      {
        title: t("Booking Form"),
        description: !!leadSalesInfo?.documents?.[0]?.Disposition
          ? `${convertToReadableString(leadSalesInfo?.documents?.[0]?.Disposition.name)}`
          : "",
        status:
          leadSalesInfo?.documents?.[0]?.Disposition?.key === "signed" ||
          leadSalesInfo?.documents?.[0]?.Disposition?.key === "spa_issued"
            ? "finish"
            : leadSalesInfo?.documents?.[0]?.Disposition?.key === "draft"
            ? "process"
            : getCurrent >= 2 || leadSalesInfo?.current >= 2
            ? "wait pending"
            : "wait",
      },
      {
        title: t("Closed(Won)"),
        description:
          leadSalesInfo?.current >= 0 || !!project ? (
            <>
              <>{`${t("CDP")}: ${
                (leadSalesInfo?.cdp || cdpCommitment) ?? project?.ProjectFinancial?.completeDownPayment
              }% `}</>
              <Separator />
              <Number
                customToolTip={{
                  title: cdp,
                }}
                {...numberProps}
                value={cdp}
              />
            </>
          ) : (
            ""
          ),
      },
      {
        title: t("V.Closed(Won)"),
        description: !!leadSalesInfo?.contracts?.[0]?.contractStatusKey
          ? `${t("Contract")}: ${convertToReadableString(leadSalesInfo?.contracts?.[0].contractStatusKey)}`
          : "",
      },
    ]
  }, [
    leadSalesInfo,
    project,
    getCurrent,
    unit,
    stateManager?.dealPrice,
    cdpCommitment,
    minTokenDetails,
    vasEnabled,
    vasFees,
    leadSalesInfo?.isVasRemaining,
  ])


  return (
    <>
      <Steps
        className={cx(cs.steps, "mbe-24")}
        current={leadSalesInfo?.current >= 0 ? leadSalesInfo?.current : getCurrent}
        data={stepData}
        progressDot
      />
      {leadSalesInfo?.ReservationQueuedAgainstLead.isReserved &&
        leadSalesInfo.ReservationQueuedAgainstLead.status === "pending" && (
          <Alert
            type="warning"
            message={`${t(
              "Submitting sales task will remove the queued request of inventory reserve for this lead against"
            )} ${leadSalesInfo?.ReservationQueuedAgainstLead?.projectName}'s ${t("Unit #")} ${
              leadSalesInfo?.ReservationQueuedAgainstLead?.unitNumber
            }.`}
          />
        )}
    </>
  )
}
export default memo(AddSaleStepper)
